// codelabels.js
//
// Codewaarde → leesbare term, per veldnaam. Bedoeld voor plekken waar we een RUWE
// opgeslagen waarde tonen zonder het bijbehorende formulier eromheen — zoals de
// activiteitentegel, die 'S' en 'gr' laat zien waar de gebruiker 'silt' en 'grijs' koos.
//
// Waarom per veld en niet één grote code→term-tabel: dezelfde letter betekent per veld
// iets anders ('S' = silt als grondsoort, siltig als bijmenging, Snel als doorloop). De
// veldnaam is dus onmisbare context.
//
// NEN 6693 en NEN 5104 (TI) gebruiken deels dezelfde letters voor verschillende termen —
// 'S' is silt in 6693 maar slib in 5104. Daarom bepaalt de standaard van het project welke
// codelijst voorrang krijgt; de andere blijft terugval, zodat oudere boringen die nog in de
// andere standaard zijn opgeslagen alsnog leesbaar blijven.

import {
  nen6693 as N, iso14688 as I, tiCodelists as T, REFERENCE_PLANES, monsterVerpakkingLabel, getBoringTypeLabel,
} from '@mv/models';

/** Zoek de term bij een code in een Map die term → code bevat. */
const termOf = (code, map) => {
  if (!map) return null;
  for (const [term, id] of map) if (id === code) return term;
  return null;
};

/** Codelijsten per veld: [NEN 6693-lijsten, NEN 5104/TI-lijst]. */
const FIELD_MAPS = {
  soilType:     [[N.GRONDSOORT, N.LAAGTYPE_SPECIAAL, N.GESTEENTE], T.bodemsoort],
  toevoeging1:  [[N.BIJMENGING], T.toevoeging],
  toevoeging2:  [[N.BIJMENGING], T.toevoeging],
  toevoeging3:  [[N.BIJMENGING], T.toevoeging],
  gradatie1:    [[N.INTENSITEIT], T.gradatie],
  gradatie2:    [[N.INTENSITEIT], T.gradatie],
  gradatie3:    [[N.INTENSITEIT], T.gradatie],
  mediaan:      [[N.MEDIAAN_KLASSE], T.mediaan],
  kleurMain:    [[N.KLEUR], T.kleur],
  kleurSub:     [[N.KLEUR], T.kleur],
  kleurShade:   [[N.KLEUR_TINT], T.kleurSterkte],
  colourMain:   [[N.KLEUR], T.kleur],
  colourSub:    [[N.KLEUR], T.kleur],
  colourShade:  [[N.KLEUR_TINT], T.kleurSterkte],
  geurType:     [[], T.geurType],
  geurIntensity: [[], T.geurSterkte],
  oilWaterReaction: [[], T.olieWaterReactie],
  stopReason:   [[], T.stopCriterium],
  // Peilbuis
  material:           [[], T.peilbuisMateriaal],
  flowType:           [[], T.peilbuisDoorloop],
  referencePointType: [[], T.peilbuisReferentievlak],
  refTopCode:         [[], T.peilbuisRefTop],
  // Watermonster
  gwsReferencePoint:  [[], T.peilbuisReferentievlak],
};

/**
 * ISO 14688 erft de NEN 6693-lijsten, maar heeft een paar eigen termen: keitjes, keiig, roze, en
 * 'teelaarde of humus'. Die lijsten eerst, anders komt bijv. 'KEI' als kale code in beeld.
 */
const ISO_MAPS = {
  soilType: [I.GRONDSOORT, I.LAAGTYPE_SPECIAAL],
  toevoeging1: [I.BIJMENGING], toevoeging2: [I.BIJMENGING], toevoeging3: [I.BIJMENGING],
  kleurMain: [I.KLEUR], kleurSub: [I.KLEUR], colourMain: [I.KLEUR], colourSub: [I.KLEUR],
};

/** Velden met een eigen label-functie in @mv/models. */
const FIELD_FN = {
  drillingSystem: (c) => N.boorsysteemLabel(c) || termOf(c, T.boorsysteem),
  groundLevelType: (c) => N.maaiveldtypeLabel(c) || termOf(c, T.groundLevelType),
  packingType: (c) => monsterVerpakkingLabel(c),
  referencePlane: (c) => REFERENCE_PLANES?.[c]?.label ?? null,
  boringType: (c) => getBoringTypeLabel(c),
  // Afwerking
  fillingMaterial: (c) => N.vulmateriaalLabel(c),
  topCompletionType: (c) => N.bovenafwerkingLabel(c),
  leftBehindType: (c) => N.achtergelatenTypeLabel(c),
};

/**
 * Leesbare term bij een opgeslagen codewaarde.
 *
 * @param {string} field     veldnaam zoals opgeslagen (bv. 'soilType')
 * @param {*} value          de ruwe waarde
 * @param {string} standard  'nen6693' (standaard) of 'nen5104' — bepaalt welke lijst wint
 * @returns {string|null}    de term, of null als er geen codelijst bij dit veld hoort of
 *                           de code er niet in voorkomt (bel dan zelf terug op de code)
 */
export function codeLabel(field, value, standard = 'nen6693') {
  if (value == null || value === '') return null;
  const code = String(value);

  const fn = FIELD_FN[field];
  if (fn) { try { return fn(code) || null; } catch (_) { return null; } }

  const entry = FIELD_MAPS[field];
  if (!entry) return null;
  const [nenMaps, tiMap] = entry;
  // Bij NEN 5104 eerst de TI-lijst; anders wint 6693 en is TI de terugval. ISO 14688: eerst de
  // eigen ISO-termen, dan die van 6693.
  const iso = standard === 'iso14688' ? (ISO_MAPS[field] || []) : [];
  const order = standard === 'nen5104' ? [tiMap, ...nenMaps] : [...iso, ...nenMaps, tiMap];
  for (const m of order) {
    const term = termOf(code, m);
    if (term) return term;
  }
  return null;
}
